import { useState, useEffect } from "react";
import { admin } from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  Plus, Trash2, X, Save, TestTube, Play, Bot, ChevronDown, ChevronUp,
  Check, AlertTriangle, ToggleLeft, ToggleRight, Copy,
} from "lucide-react";
import clsx from "clsx";

// ── Types ────────────────────────────────────────────────────

interface VerticalPrompt {
  id: string;
  vertical: string;
  name: string;
  persona: string;
  persona_email: string;
  persona_phone: string | null;
  brand: string;
  system_prompt: string;
  replyable_intents: string[];
  silent_intents: string[];
  enabled: number;
  created_at: string;
  updated_at: string;
}

interface TestResult {
  intent: string;
  wouldReply: boolean;
  generatedReply: string;
  systemPromptPreview: string;
}

const ALL_INTENTS = ["quote_request", "interested", "question", "decline", "auto_ack", "spam", "unknown"];

// ── Main Page ────────────────────────────────────────────────

export default function AdminVerticalsPage() {
  const [verticals, setVerticals] = useState<VerticalPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: "ok" | "err" } | null>(null);
  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 4000); return () => clearTimeout(t); } }, [toast]);

  async function load() {
    try {
      const data = await admin.verticals.list();
      setVerticals(data.verticals);
    } catch (err: any) {
      setToast({ msg: "Failed to load verticals", type: "err" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete vertical "${name}"? This cannot be undone.`)) return;
    try {
      await admin.verticals.delete(id);
      setVerticals(prev => prev.filter(v => v.id !== id));
      setToast({ msg: `Deleted ${name}`, type: "ok" });
    } catch { setToast({ msg: "Delete failed", type: "err" }); }
  }

  async function handleToggle(v: VerticalPrompt) {
    try {
      const updated = await admin.verticals.update(v.id, { enabled: v.enabled ? 0 : 1 });
      setVerticals(prev => prev.map(x => x.id === v.id ? updated.vertical : x));
      setToast({ msg: `${v.name} ${v.enabled ? "disabled" : "enabled"}`, type: "ok" });
    } catch { setToast({ msg: "Toggle failed", type: "err" }); }
  }

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-ink">Vertical Prompts</h1>
          <p className="text-sm text-slate mt-1">Manage auto-reply personas and prompts per vertical</p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-1.5 bg-ink text-white text-sm font-semibold px-4 py-2 rounded-xl hover:opacity-90 transition"
        >
          {showCreate ? <X size={14} /> : <Plus size={14} />}
          {showCreate ? "Cancel" : "New Vertical"}
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <CreateVerticalForm
          onCreated={(v) => { setVerticals(prev => [v, ...prev]); setShowCreate(false); setToast({ msg: `Created ${v.name}`, type: "ok" }); }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* List */}
      {verticals.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-card p-10 text-center text-slate">
          No verticals configured. Create one to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {verticals.map(v => (
            <div key={v.id} className="bg-white rounded-2xl shadow-card overflow-hidden">
              {/* Collapsed row */}
              <div
                className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-black/[0.015] transition"
                onClick={() => setExpandedId(expandedId === v.id ? null : v.id)}
              >
                <Bot size={18} className={v.enabled ? "text-teal" : "text-slate/40"} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-ink">{v.name}</span>
                    <span className="text-[10px] font-mono text-slate bg-black/[0.04] px-1.5 py-0.5 rounded">{v.vertical}</span>
                    {!v.enabled && <span className="text-[10px] font-semibold text-red-500 bg-red-50 px-1.5 py-0.5 rounded">Disabled</span>}
                  </div>
                  <p className="text-xs text-slate mt-0.5">{v.persona} · {v.persona_email} · {v.brand}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleToggle(v); }}
                  className="text-slate hover:text-ink transition"
                  title={v.enabled ? "Disable" : "Enable"}
                >
                  {v.enabled ? <ToggleRight size={22} className="text-teal" /> : <ToggleLeft size={22} />}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(v.id, v.name); }}
                  className="text-slate hover:text-red-500 transition"
                >
                  <Trash2 size={15} />
                </button>
                {expandedId === v.id ? <ChevronUp size={16} className="text-slate" /> : <ChevronDown size={16} className="text-slate" />}
              </div>

              {/* Expanded edit + test */}
              {expandedId === v.id && (
                <VerticalEditor
                  vertical={v}
                  onSaved={(updated) => {
                    setVerticals(prev => prev.map(x => x.id === updated.id ? updated : x));
                    setToast({ msg: "Saved", type: "ok" });
                  }}
                  saving={saving}
                  setSaving={setSaving}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={clsx(
          "fixed bottom-6 right-6 px-4 py-2.5 rounded-xl shadow-lg text-sm font-semibold z-50 animate-in slide-in-from-bottom-2",
          toast.type === "ok" ? "bg-teal text-white" : "bg-red-500 text-white"
        )}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ── Create Form ──────────────────────────────────────────────

function CreateVerticalForm({ onCreated, onCancel }: { onCreated: (v: VerticalPrompt) => void; onCancel: () => void }) {
  const [form, setForm] = useState({
    vertical: "",
    name: "",
    persona: "",
    persona_email: "",
    persona_phone: "",
    brand: "",
    system_prompt: "",
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  async function handleCreate() {
    if (!form.vertical || !form.name || !form.persona || !form.persona_email || !form.brand || !form.system_prompt) {
      setError("All fields except phone are required");
      return;
    }
    setCreating(true);
    setError("");
    try {
      const data = await admin.verticals.create(form);
      onCreated(data.vertical);
    } catch (err: any) {
      setError(err.message || "Failed to create");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-card p-6 space-y-4">
      <h2 className="font-bold text-ink text-lg">New Vertical</h2>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-slate mb-1">Vertical Key</label>
          <input className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm" placeholder="readymove"
            value={form.vertical} onChange={e => setForm({ ...form, vertical: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })} />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate mb-1">Display Name</label>
          <input className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm" placeholder="ReadyMove"
            value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate mb-1">Persona Name</label>
          <input className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm" placeholder="Emma Lee"
            value={form.persona} onChange={e => setForm({ ...form, persona: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate mb-1">Persona Email</label>
          <input className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm" placeholder="emma@readymove.com"
            value={form.persona_email} onChange={e => setForm({ ...form, persona_email: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate mb-1">Persona Phone</label>
          <input className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm" placeholder="7864180620"
            value={form.persona_phone} onChange={e => setForm({ ...form, persona_phone: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate mb-1">Brand</label>
          <input className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm" placeholder="ReadyMove"
            value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} />
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold text-slate mb-1">System Prompt</label>
        <textarea className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm font-mono min-h-[200px]" placeholder="You are Emma Lee, a person who..."
          value={form.system_prompt} onChange={e => setForm({ ...form, system_prompt: e.target.value })} />
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      <div className="flex gap-2">
        <button onClick={handleCreate} disabled={creating}
          className="flex items-center gap-1.5 bg-teal text-white text-sm font-semibold px-4 py-2 rounded-xl hover:opacity-90 transition disabled:opacity-50">
          {creating ? <Spinner /> : <Plus size={14} />} Create
        </button>
        <button onClick={onCancel} className="text-sm text-slate hover:text-ink transition px-3 py-2">Cancel</button>
      </div>
    </div>
  );
}

// ── Editor (expanded) ────────────────────────────────────────

function VerticalEditor({ vertical: v, onSaved, saving, setSaving }: {
  vertical: VerticalPrompt;
  onSaved: (v: VerticalPrompt) => void;
  saving: string | null;
  setSaving: (s: string | null) => void;
}) {
  const [form, setForm] = useState({
    name: v.name,
    persona: v.persona,
    persona_email: v.persona_email,
    persona_phone: v.persona_phone || "",
    brand: v.brand,
    system_prompt: v.system_prompt,
    replyable_intents: v.replyable_intents || [],
    silent_intents: v.silent_intents || [],
  });

  const [testEmail, setTestEmail] = useState("");
  const [testSubject, setTestSubject] = useState("Re: Hello Inquiry about services");
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);

  const dirty = form.name !== v.name || form.persona !== v.persona || form.persona_email !== v.persona_email
    || form.persona_phone !== (v.persona_phone || "") || form.brand !== v.brand || form.system_prompt !== v.system_prompt
    || JSON.stringify(form.replyable_intents) !== JSON.stringify(v.replyable_intents)
    || JSON.stringify(form.silent_intents) !== JSON.stringify(v.silent_intents);

  async function handleSave() {
    setSaving(v.id);
    try {
      const data = await admin.verticals.update(v.id, form);
      onSaved(data.vertical);
    } catch { }
    setSaving(null);
  }

  async function handleTest() {
    if (!testEmail.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const data = await admin.verticals.test(v.id, {
        sample_email: testEmail,
        sample_subject: testSubject,
        sender_name: "John Smith",
        sender_email: "john@movingco.com",
      });
      setTestResult(data);
    } catch (err: any) {
      setTestResult({ intent: "error", wouldReply: false, generatedReply: err.message || "Test failed", systemPromptPreview: "" });
    }
    setTesting(false);
  }

  function toggleIntent(intent: string) {
    const inReplyable = form.replyable_intents.includes(intent);
    const inSilent = form.silent_intents.includes(intent);

    if (inReplyable) {
      // Move to silent
      setForm({
        ...form,
        replyable_intents: form.replyable_intents.filter(i => i !== intent),
        silent_intents: [...form.silent_intents, intent],
      });
    } else if (inSilent) {
      // Move to replyable
      setForm({
        ...form,
        silent_intents: form.silent_intents.filter(i => i !== intent),
        replyable_intents: [...form.replyable_intents, intent],
      });
    } else {
      // Not in either, add to replyable
      setForm({ ...form, replyable_intents: [...form.replyable_intents, intent] });
    }
  }

  return (
    <div className="border-t border-black/[0.04] px-5 py-5 space-y-5">
      {/* Fields */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-[10px] font-bold text-slate uppercase tracking-wider mb-1">Name</label>
          <input className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm"
            value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate uppercase tracking-wider mb-1">Persona</label>
          <input className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm"
            value={form.persona} onChange={e => setForm({ ...form, persona: e.target.value })} />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate uppercase tracking-wider mb-1">Brand</label>
          <input className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm"
            value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate uppercase tracking-wider mb-1">Persona Email</label>
          <input className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm"
            value={form.persona_email} onChange={e => setForm({ ...form, persona_email: e.target.value })} />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate uppercase tracking-wider mb-1">Persona Phone</label>
          <input className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm"
            value={form.persona_phone} onChange={e => setForm({ ...form, persona_phone: e.target.value })} />
        </div>
      </div>

      {/* Intent toggles */}
      <div>
        <label className="block text-[10px] font-bold text-slate uppercase tracking-wider mb-2">Intent Routing</label>
        <div className="flex flex-wrap gap-2">
          {ALL_INTENTS.map(intent => {
            const isReply = form.replyable_intents.includes(intent);
            const isSilent = form.silent_intents.includes(intent);
            return (
              <button key={intent} onClick={() => toggleIntent(intent)}
                className={clsx(
                  "text-xs font-semibold px-3 py-1.5 rounded-lg transition border",
                  isReply ? "border-green-300 bg-green-50 text-green-700" :
                  isSilent ? "border-red-200 bg-red-50 text-red-600" :
                  "border-black/10 bg-white text-slate"
                )}>
                {isReply ? "✓ Reply" : isSilent ? "✕ Silent" : "?"} · {intent.replace(/_/g, " ")}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-slate mt-1">Click to toggle between Reply (green) and Silent (red)</p>
      </div>

      {/* System prompt */}
      <div>
        <label className="block text-[10px] font-bold text-slate uppercase tracking-wider mb-1">System Prompt</label>
        <textarea
          className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm font-mono min-h-[300px] leading-relaxed"
          value={form.system_prompt}
          onChange={e => setForm({ ...form, system_prompt: e.target.value })}
        />
        <p className="text-[10px] text-slate mt-1">{form.system_prompt.length.toLocaleString()} characters</p>
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={!dirty || saving === v.id}
          className={clsx(
            "flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl transition",
            dirty ? "bg-teal text-white hover:opacity-90" : "bg-black/[0.04] text-slate cursor-not-allowed"
          )}>
          {saving === v.id ? <Spinner /> : <Save size={14} />}
          {dirty ? "Save Changes" : "No Changes"}
        </button>
      </div>

      {/* Test section */}
      <div className="border-t border-black/[0.04] pt-5 space-y-3">
        <h3 className="text-sm font-bold text-ink flex items-center gap-1.5"><TestTube size={14} /> Test Prompt</h3>
        <p className="text-xs text-slate">Paste a sample email reply to see how the AI would respond</p>
        <div className="grid grid-cols-1 gap-3">
          <input className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm" placeholder="Subject: Re: Moving inquiry"
            value={testSubject} onChange={e => setTestSubject(e.target.value)} />
          <textarea className="w-full border border-black/10 rounded-lg px-3 py-2 text-sm min-h-[100px]"
            placeholder="Hi, thanks for reaching out! We'd love to help. Can you tell us the SQFT of your home and what floor you're on?"
            value={testEmail} onChange={e => setTestEmail(e.target.value)} />
        </div>
        <button onClick={handleTest} disabled={testing || !testEmail.trim()}
          className="flex items-center gap-1.5 bg-ink text-white text-sm font-semibold px-4 py-2 rounded-xl hover:opacity-90 transition disabled:opacity-50">
          {testing ? <Spinner /> : <Play size={14} />} Run Test
        </button>

        {testResult && (
          <div className="bg-black/[0.02] rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className={clsx(
                "text-xs font-bold px-2 py-1 rounded-lg",
                testResult.wouldReply ? "text-green-700 bg-green-50" : "text-red-600 bg-red-50"
              )}>
                {testResult.wouldReply ? "Would Reply" : "Would Skip"}
              </span>
              <span className="text-xs font-semibold text-slate bg-black/[0.04] px-2 py-1 rounded-lg">
                Intent: {testResult.intent.replace(/_/g, " ")}
              </span>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate uppercase tracking-wider mb-1">Generated Reply</p>
              <pre className="text-sm whitespace-pre-wrap text-ink bg-white rounded-lg p-3 border border-black/[0.06]">
                {testResult.generatedReply}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
